import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxCodeEndpoints } from '@/lib/api/endpoints';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';

/**
 * Create a new manual tax code. Used by the inline edit dialog when the
 * pencil is clicked on a level with no matching code (e.g. NJ county
 * with 0% sales tax, or a fresh state with no city-level code yet —
 * Task 3).
 */
export interface CreateTaxCodePayload {
  name: string;
  rate: number;
  rateType?: 'percentage' | 'fixed';
  /** Multi-dim applies-to (Brian's backlog #41). Single string accepted for back-compat. */
  appliesTo?:
    | 'all'
    | 'materials'
    | 'labor'
    | 'freight'
    | Array<'all' | 'materials' | 'labor' | 'freight'>;
  jurisdiction?: string;
  jurisdictionType?: 'state' | 'county' | 'city' | 'district';
  stateCode?: string;
  registrationNumber?: string;
  isActive?: boolean;
  /**
   * Optional taxGroup id to link the new code into in the same backend
   * transaction. The inline pencil flow on /dashboard/taxes uses this
   * when the user adds a missing tier (e.g. city) to an existing ZIP
   * group — without it the BE would orphan the tax_code and the row
   * would never reflect the saved rate (the saved code wouldn't be
   * a member of the resolved tax_group).
   */
  groupId?: string;
  /**
   * Fork-on-modify list. When set together with `groupId`, the BE
   * clones the source group, re-points only these ZIPs to the clone,
   * and adds the new code on the clone — so the customization stays
   * scoped to the chosen ZIPs even when many ZIPs originally shared
   * the same Ziptax-populated group (the Nassau city-leak case).
   * Omitting it preserves the legacy linked-create propagation.
   */
  forkForZipCodes?: string[];
}

export function useCreateTaxCode() {
  const qc = useQueryClient();
  return useApiMutation<{ id: string }, CreateTaxCodePayload>({
    mutationFn: (payload) =>
      apiPost<{ id: string }>(operationsClient, taxCodeEndpoints.create(), payload),
    onSuccess: () => {
      // Reads "Tax rate created" intentionally — this hook fires when
      // the user adds a new manual code via the inline pencil dialog,
      // which is the rate-create flow. Pair with `useUpdateTaxCode`
      // (toast: "Tax rate updated") so the override-edit path doesn't
      // tell the user it "created" a new code (it didn't).
      toast.success('Tax rate created.');
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
    },
  });
}
